import { useMemo, useRef, useState } from 'react'

import { Formik } from 'formik'
import type { FormikHelpers } from 'formik'
import {
  useCreateManualRentalHistory,
  useCreateRentalHistory,
  useUpdateManualRentalHistory,
  useUpdateRentalHistory,
} from 'hooks/useRentalHistoryQueries'
import qs from 'qs'

import { API_ERRORS_PROPERTY } from 'app/forms/FieldWrapper'
import { convertTransApiErrorToStatus } from 'app/forms/utils/convertTransApiErrorToStatus'
import { formValidationBuilder } from 'app/forms/utils/formValidationBuilder'
import {
  buildManualRentalHistoryPayload,
  buildRequestPayload,
  fieldValidation,
  getDefaultFormValues,
  transformApiErrorToFields,
  transformManualRentalHistoryToFormValues,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import { FormBody } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/FormBody'
import type { ApplicationApplicant } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/TenantsSection'
import type {
  ManualRentalHistoryEditData,
  RentalHistoryFormValues,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import {
  deleteRentalLedger,
  deleteUnboundedLedger,
  uploadLedgerByRenter,
  uploadUnboundedLedgerByRenter,
} from 'app/services/http/rentalReferences'
import * as snugNotifier from 'app/services/snugNotifier'
import { composeAttachment } from 'app/shared_components/helpers'

export interface RentalHistoryFormProps {
  isEditMode?: boolean
  initialData?: Partial<RentalHistoryFormValues> & { id?: string }
  editData?: ManualRentalHistoryEditData // For manual rental history edit mode
  onSuccess?: (data: any) => void
  onError?: (error: string) => void
  isQLDForm22?: boolean
  // Manual rental history props
  isManualMode?: boolean
  applicantGUID?: string
  agencyGUID?: string
  // Applicants data for auto-fill functionality
  applicants?: ApplicationApplicant[]
  // Control props
  hideSubmitButton?: boolean
  // Ledger callback props for immediate UI updates
  onLedgerUpload?: (ledgers: any[]) => void
  onLedgerDelete?: (guidID: string) => void
  // Status change callback for reactive UI
  onStatusChange?: (isLoading: boolean, canSubmit: boolean) => void
  // Submit handler callback
  onSubmitReady?: (submitHandler: () => void) => void
}

export const RentalHistoryForm = ({
  isEditMode = false,
  initialData = {},
  editData,
  onSuccess,
  onError,
  isQLDForm22 = false,
  isManualMode = false,
  applicantGUID = '',
  agencyGUID = '',
  applicants = [],
  hideSubmitButton = false,
  onLedgerUpload,
  onLedgerDelete,
  onStatusChange,
  onSubmitReady,
}: RentalHistoryFormProps) => {
  // API mutations
  const createMutation = useCreateRentalHistory()
  const createManualMutation = useCreateManualRentalHistory()
  const updateMutation = useUpdateRentalHistory()
  const updateManualMutation = useUpdateManualRentalHistory()

  // Ref for the formik instance
  const formikRef = useRef<any>(null)

  // State for tracking document operations
  const [isDocumentLoading, setIsDocumentLoading] = useState(false)

  // Check if any mutation is loading
  const isLoading =
    createMutation.isPending ||
    createManualMutation.isPending ||
    updateMutation.isPending ||
    updateManualMutation.isPending

  // Initial form values - memoized to recompute when dependencies change
  const initialValues: RentalHistoryFormValues = useMemo(() => {
    const getInitialValues = (): RentalHistoryFormValues => {
      let baseValues = getDefaultFormValues()

      // For manual edit mode, transform edit data
      if (isManualMode && isEditMode && editData) {
        const transformedValues =
          transformManualRentalHistoryToFormValues(editData)
        baseValues = { ...baseValues, ...transformedValues }
      }

      // Apply any additional initial data
      const finalValues = {
        ...baseValues,
        ...initialData,
        isQLDForm22,
      }

      return finalValues
    }

    return getInitialValues()
  }, [isManualMode, isEditMode, editData, initialData, isQLDForm22])

  // Form submission handler - following pm-messages pattern
  const handleSubmit = async (
    values: RentalHistoryFormValues,
    { setSubmitting, setStatus }: FormikHelpers<RentalHistoryFormValues>,
  ) => {
    try {
      setSubmitting(true)
      setStatus({ apiErrors: {}, apiGeneralError: '' })

      let result

      if (isManualMode) {
        // Extract primary applicant details
        const primaryApplicant = applicants?.find(
          (applicant) => applicant.isPrimary,
        )

        if (!primaryApplicant) {
          throw new Error(
            'Primary applicant details are required for manual rental history',
          )
        }

        const applicantDetails = {
          firstName: primaryApplicant.firstName,
          lastName: primaryApplicant.lastName,
          email: primaryApplicant.email,
          mobileNumber: primaryApplicant.mobileNumber,
        }

        // Build manual API payload
        const payload = buildManualRentalHistoryPayload(
          values,
          applicantGUID,
          agencyGUID,
          applicantDetails,
          isQLDForm22,
        )

        // Call appropriate manual API (create vs update)
        result =
          isEditMode && editData?.guidID
            ? await updateManualMutation.mutateAsync({
                guidID: editData.guidID,
                rentalHistoryData: payload,
              })
            : await createManualMutation.mutateAsync({
                rentalHistoryData: payload,
              })
      } else {
        // Build regular API payload
        const payload = buildRequestPayload(values, isQLDForm22)

        // Call appropriate API
        result =
          isEditMode && initialData.id
            ? await updateMutation.mutateAsync({
                id: initialData.id,
                rentalHistoryData: payload,
              })
            : await createMutation.mutateAsync({
                rentalHistoryData: payload,
              })
      }

      // Handle success
      onSuccess?.(result)
    } catch (error: any) {
      // Handle API errors - following pm-messages pattern
      if (error.detailedError) {
        const transformedErrors = transformApiErrorToFields(error.detailedError)
        setStatus({
          [API_ERRORS_PROPERTY]:
            convertTransApiErrorToStatus(transformedErrors),
        })
      } else {
        const errorMessage = error.message || 'An unexpected error occurred'
        setStatus({ apiGeneralError: errorMessage })
        onError?.(errorMessage)
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Document removal handler
  const handleRemoveLedgerAttachment = (guidID: string, event?: any) => {
    // Prevent any event bubbling that might cause form submission or navigation
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    setIsDocumentLoading(true)

    // Prevent form submission/navigation by using API delete
    let deletePromise
    if (isEditMode && editData?.guidID) {
      deletePromise = deleteRentalLedger(editData.guidID, guidID)
    } else {
      deletePromise = deleteUnboundedLedger(guidID)
    }

    deletePromise
      .then(() => {
        snugNotifier.success('Successfully deleted ledger file')

        // Remove from form state immediately and force re-render
        if (formikRef.current) {
          const currentAttachments =
            formikRef.current.values.ledgerAttachments || []
          const updatedAttachments = currentAttachments.filter(
            (attachment: any) => attachment.guidID !== guidID,
          )

          // Update form values immediately - use setTimeout to ensure React processes the update
          formikRef.current.setFieldValue(
            'ledgerAttachments',
            updatedAttachments,
            false, // Don't validate
          )

          // Use setTimeout to ensure the state update is processed in the next tick
          setTimeout(() => {
            if (formikRef.current) {
              formikRef.current.setValues(
                {
                  ...formikRef.current.values,
                  ledgerAttachments: updatedAttachments,
                },
                false,
              )
            }
          }, 0)
        }

        // Notify parent component immediately (similar to handleManualRentalHistorySuccess pattern)
        if (onLedgerDelete) {
          onLedgerDelete(guidID)
        }
      })
      .catch((error: any) => {
        snugNotifier.error(error.message || 'Failed to delete file')
      })
      .finally(() => {
        setIsDocumentLoading(false)
      })
  }

  // Ledger upload handler based on original rental_history_form.jsx
  const handleUploadLedgerAttachment = (documentType: string) => {
    const attachments = document.getElementById(`attachments-${documentType}`)
    if (!attachments) {
      snugNotifier.error('File input not found')
      return
    }

    setIsDocumentLoading(true)
    const formData = new FormData()
    composeAttachment(attachments, formData)

    // Get agency GUID from URL parameters (similar to original implementation)
    const curQueries = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })
    const { agencyGUID = '' } = curQueries || {}

    let uploadPromise
    if (isEditMode && editData?.guidID) {
      uploadPromise = uploadLedgerByRenter(
        editData.guidID,
        formData,
        agencyGUID,
      )
    } else {
      uploadPromise = uploadUnboundedLedgerByRenter(formData, agencyGUID)
    }

    uploadPromise
      .then(({ ledgers = [] }) => {
        snugNotifier.success('Successfully uploaded ledger files')

        // Add the uploaded ledgers to the form values immediately and force re-render
        if (formikRef.current && ledgers.length > 0) {
          const currentAttachments =
            formikRef.current.values.ledgerAttachments || []
          const newAttachments = [...currentAttachments, ...ledgers]

          // Update form values immediately - use setTimeout to ensure React processes the update
          formikRef.current.setFieldValue(
            'ledgerAttachments',
            newAttachments,
            false, // Don't validate
          )

          // Use setTimeout to ensure the state update is processed in the next tick
          setTimeout(() => {
            if (formikRef.current) {
              formikRef.current.setValues(
                {
                  ...formikRef.current.values,
                  ledgerAttachments: newAttachments,
                },
                false,
              )
            }
          }, 0)
        }

        // Notify parent component immediately (similar to handleManualRentalHistorySuccess pattern)
        if (onLedgerUpload && ledgers.length > 0) {
          onLedgerUpload(ledgers)
        }
      })
      .catch((error: any) => {
        snugNotifier.error(error.message || 'Upload failed')
      })
      .finally(() => {
        setIsDocumentLoading(false)
      })
  }

  // Custom validation function that has access to isQLDForm22
  const validateWithQLDForm22 = (values: RentalHistoryFormValues) => {
    return formValidationBuilder(
      (fieldName: string, val: any, formData?: RentalHistoryFormValues) => {
        // Pass isQLDForm22 context by temporarily adding it to formData for validation
        const formDataWithQLDFlag = formData
          ? { ...formData, isQLDForm22 }
          : ({ isQLDForm22 } as any)
        return fieldValidation(fieldName, val, formDataWithQLDFlag)
      },
    )(values)
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validate={validateWithQLDForm22}
      validateOnBlur={true}
      onSubmit={handleSubmit}
    >
      <FormBody
        isLoading={isLoading}
        isDocumentLoading={isDocumentLoading}
        removeLedgerAttachment={handleRemoveLedgerAttachment}
        uploadLedgerAttachment={handleUploadLedgerAttachment}
        hideSubmitButton={hideSubmitButton}
        isEditMode={isEditMode}
        applicants={applicants}
        isQLDForm22={isQLDForm22}
        {...(onStatusChange && { onStatusChange })}
        {...(onSubmitReady && { onSubmitReady })}
      />
    </Formik>
  )
}
